import React, { useEffect, useState, useRef } from 'react';
import supabase from '../lib/supabase';

function AudioPlayer({ currentSound }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentSoundRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);

  useEffect(() => {
    if (currentSound && currentSound.timestamp !== currentSoundRef.current?.timestamp) {
      currentSoundRef.current = currentSound;
      loadAndPlayAudio();
    }
  }, [currentSound]);

  const stopAllAudio = () => {
    // Clear any pending cleanup timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Stop HTML Audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }

    // Stop Speech Synthesis immediately
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    // Reset playing states immediately
    isPlayingRef.current = false;
    setIsPlaying(false);
  };

  const loadAndPlayAudio = async () => {
    if (!currentSound) return;

    try {
      // Always stop current audio first, regardless of playing state
      stopAllAudio();
      
      console.log(`Loading audio for: ${currentSound.example}`);

      // Set playing state immediately
      isPlayingRef.current = true;
      setIsPlaying(true);

      // Shorter delay - just enough to ensure cleanup
      await new Promise(resolve => setTimeout(resolve, 50));

      // Double-check we should still play
      if (!isPlayingRef.current || currentSound.timestamp !== currentSoundRef.current?.timestamp) {
        console.log('Audio request cancelled during delay');
        return;
      }

      // Try to get audio from Supabase storage first
      const audioFileName = `${currentSound.example.toLowerCase().replace(/[^a-z0-9]/g, '')}.mp3`;
      
      try {
        const { data, error } = await supabase.storage
          .from('audio-files')
          .getPublicUrl(audioFileName);

        if (!error && data?.publicUrl && isPlayingRef.current) {
          console.log(`Found audio file in Supabase: ${audioFileName}`);
          await playAudioFromUrl(data.publicUrl);
          return;
        }
      } catch (storageError) {
        console.log('Supabase storage not available, falling back to TTS');
      }

      // Fallback to Text-to-Speech
      if (isPlayingRef.current && currentSound.timestamp === currentSoundRef.current?.timestamp) {
        console.log('Using TTS for:', currentSound.example);
        await playWithTTS(currentSound.example);
      }

    } catch (error) {
      console.error('Audio loading error:', error);
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  };

  const playAudioFromUrl = async (url) => {
    return new Promise((resolve, reject) => {
      if (!isPlayingRef.current) {
        resolve();
        return;
      }

      const audio = new Audio();
      audioRef.current = audio;

      const cleanup = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
        isPlayingRef.current = false;
        setIsPlaying(false);
      };

      audio.oncanplaythrough = () => {
        if (!isPlayingRef.current || audioRef.current !== audio) {
          cleanup();
          resolve();
          return;
        }
        
        console.log('Audio ready, playing...');
        audio.play()
          .then(() => {
            console.log('Audio playback started successfully');
          })
          .catch(error => {
            console.error('Playback failed:', error);
            cleanup();
            reject(error);
          });
      };

      audio.onended = () => {
        console.log('Audio playback ended');
        cleanup();
        resolve();
      };

      audio.onerror = (error) => {
        console.error('Audio load error:', error);
        cleanup();
        reject(error);
      };

      audio.onabort = () => {
        console.log('Audio loading aborted');
        cleanup();
        resolve();
      };

      // Set properties and load
      audio.preload = 'auto';
      audio.src = url;
      audio.load();

      // Timeout fallback in case audio doesn't load
      cleanupTimeoutRef.current = setTimeout(() => {
        if (audioRef.current === audio && isPlayingRef.current) {
          console.log('Audio loading timeout, falling back to TTS');
          cleanup();
          reject(new Error('Audio loading timeout'));
        }
      }, 3000);
    });
  };

  const playWithTTS = async (text) => {
    return new Promise((resolve, reject) => {
      if (!isPlayingRef.current) {
        resolve();
        return;
      }

      if ('speechSynthesis' in window) {
        // Ensure speech synthesis is ready
        const speak = () => {
          const utterance = new SpeechSynthesisUtterance(text);
          
          // Configure speech settings for clearer pronunciation
          utterance.rate = 0.6;
          utterance.pitch = 1.0;
          utterance.volume = 1.0;

          // Try to use a high-quality English voice
          const voices = window.speechSynthesis.getVoices();
          const englishVoice = voices.find(voice => 
            voice.lang.startsWith('en') && 
            (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.name.includes('Apple'))
          ) || voices.find(voice => voice.lang.startsWith('en'));

          if (englishVoice) {
            utterance.voice = englishVoice;
          }

          utterance.onstart = () => {
            console.log('TTS started for:', text);
          };

          utterance.onend = () => {
            console.log('TTS ended for:', text);
            isPlayingRef.current = false;
            setIsPlaying(false);
            resolve();
          };

          utterance.onerror = (error) => {
            console.error('TTS error:', error);
            isPlayingRef.current = false;
            setIsPlaying(false);
            reject(error);
          };

          // Final check before speaking
          if (isPlayingRef.current) {
            window.speechSynthesis.speak(utterance);
          } else {
            resolve();
          }
        };

        // Handle voices loading
        if (window.speechSynthesis.getVoices().length > 0) {
          speak();
        } else {
          window.speechSynthesis.onvoiceschanged = () => {
            speak();
          };
          
          // Fallback timeout
          setTimeout(() => {
            if (isPlayingRef.current) {
              speak();
            }
          }, 100);
        }
      } else {
        console.error('Speech synthesis not supported');
        isPlayingRef.current = false;
        setIsPlaying(false);
        reject(new Error('Speech synthesis not supported'));
      }
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, []);

  return null;
}

export default AudioPlayer;